import { useEffect, useMemo, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import TemplatePicker from './TemplatePicker.jsx';
import PreviewPane from './PreviewPane.jsx';
import { recommendedForContext } from './stageTemplates.js';
import {
  getSendTemplates,
  previewTemplate,
  sendTemplate,
  getSendTemplatesForDeal,
  previewTemplateForDeal,
  sendTemplateForDeal,
  sendSMS,
  resolvePhone,
} from '@/services/comms.service.js';
import {
  smsDefaultFor,
  smsSegments,
  SMS_SINGLE_SEGMENT,
  SMS_MAX_LENGTH,
} from './smsDefaults.js';
import { ApiError } from '@/lib/api.js';
import { toastSmsWarnings } from '@/lib/smsWarnings.js';
import { getPresentableQuotes } from '@/services/quotes.service.js';
import { formatCurrency } from '@/lib/format.js';

// The Comms Hub compose surface. Opens as a bottom sheet on mobile and
// a wider right-slide on desktop. Three logical sections:
//   1. Template picker (left column on desktop, top on mobile)
//   2. Subject + recipient overrides
//   3. Live preview (iframe srcDoc)
//
// V1 scope:
//   - Defaults pulled from /preview/:slug response (to, toName, subject)
//   - Broker can override recipient and subject before send
//   - No dry-run toggle. EMAIL_OVERRIDE, when set on Render, redirects
//     every send, these included (2026-10-14).
//   - urgency / ai_name / ai_email / pilot_crm_id collected as a
//     single optional Broker Note field for V1; structured form fields
//     for those templates are V1.5 polish.

export default function ComposeSheet({
  sessionToken,
  // dealId enables the SESSION-OPTIONAL path: a deal with no client session
  // still opens Compose and sends the deal-renderable broker templates
  // (rendered from CRM, sent + logged deal-keyed). Session present → the
  // existing session path (carries the client tower link) is used unchanged.
  dealId = null,
  open,
  onOpenChange,
  initialSlug = null,
  // EITHER pass recommendedSlugs directly (legacy callers) OR pass
  // dealContext = {stage, daysUntilExpiration, daysSinceLastActivity}
  // and let the recommender escalate slugs by timing (Vision §V2 #2).
  recommendedSlugs = null,
  dealContext = null,
  // Optional extras forwarded to the preview endpoint (?pilot_id=,
  // ?urgency=, ?ai_email=, ?ai_name=) and to the send body
  // (pilot_crm_id, urgency, ai_name, ai_email). Used by the
  // RosterPanel per-pilot Remind button (Vision §V2 fix #2 2026-05-20)
  // so the preview + send target the SPECIFIC pilot the broker
  // clicked rather than the auto-picked first-Pending PHF.
  extra = null,
  onSent,
}) {
  // Session present → session-keyed send (carries the client tower link).
  // No session but a dealId → deal-keyed send (CRM-rendered broker templates).
  const useSession = !!sessionToken;
  const canCompose = !!sessionToken || !!dealId;

  const derivedRecommended = useMemo(() => {
    if (Array.isArray(recommendedSlugs)) return recommendedSlugs;
    const base = dealContext ? recommendedForContext(dealContext) : [];
    // When the caller hands off an explicit template (e.g. Save+Present
    // → 'quotes-ready'), pin that slug as the primary recommendation so
    // the picker doesn't mis-suggest a stage-based default (a Bound
    // RENEWAL_DUE deal whose renewal we're actively quoting would
    // otherwise suggest 'renewal-invitation').
    if (initialSlug) {
      return [initialSlug, ...base.filter((s) => s !== initialSlug)];
    }
    return base;
  }, [recommendedSlugs, dealContext, initialSlug]);
  const [templates, setTemplates] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [slug, setSlug] = useState(initialSlug);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [subject, setSubject] = useState('');
  const [recipient, setRecipient] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // §7 Slice 5 (gap B) — the binding-app-ready ("select & send checkout")
  // template needs the broker to pick WHICH presented quote; the checkout link
  // carries ?quote=<id>. presentableQuotes is the deal's client-visible envelope
  // quotes; quoteId is the broker's pick (merged into extra as quote_id).
  const [presentableQuotes, setPresentableQuotes] = useState([]);
  const [quoteId, setQuoteId] = useState('');
  const effectiveExtra = useMemo(
    () => ({ ...(extra || {}), ...(quoteId ? { quote_id: quoteId } : {}) }),
    [extra, quoteId],
  );

  // ─── SMS channel (Gap 2) ────────────────────────────────────────
  // Channel toggle. SMS mode resolves a read-only E.164 number, hides the
  // subject, and sends a freeform plain-text body (prefilled per slug). The
  // SMS option is disabled when no mobile is on file for the recipient.
  const [channel, setChannel] = useState('email');
  const [smsPhone, setSmsPhone] = useState(null);
  const [smsOptedOut, setSmsOptedOut] = useState(false);
  const [smsResolved, setSmsResolved] = useState(false);
  const [smsBody, setSmsBody] = useState('');
  // Contact id for phone resolution: an explicit pilot context wins, else the
  // deal's primary contact from the rendered preview.
  const pilotId = extra?.pilot_id || extra?.pilot_crm_id || null;
  const smsContactId = pilotId || preview?.contact_crm_id || null;
  const smsFirstName = (preview?.toName || '').trim().split(/\s+/)[0] || '';
  const smsSegmentCount = smsSegments(smsBody);

  // Reset state when the sheet opens or session changes.
  useEffect(() => {
    if (!open) return;
    setSlug(initialSlug);
    setPreview(null);
    setSubject('');
    setRecipient('');
    setChannel('email');
    setSmsPhone(null);
    setSmsOptedOut(false);
    setSmsResolved(false);
    setSmsBody('');
    if (!canCompose) return;
    setLoadingTemplates(true);
    const load = useSession
      ? getSendTemplates(sessionToken)
      : getSendTemplatesForDeal(dealId);
    load
      .then((r) => setTemplates(r?.templates || []))
      .catch(() => setTemplates([]))
      .finally(() => setLoadingTemplates(false));
  }, [open, sessionToken, dealId, useSession, canCompose, initialSlug]);

  // §7 Slice 5 (gap B) — when the broker picks the "Binding App Ready (Checkout)"
  // template, load the deal's presentable quotes and default to the first so the
  // checkout link always carries a ?quote=<id>.
  useEffect(() => {
    if (!open || slug !== 'binding-app-ready' || !dealId) {
      setPresentableQuotes([]);
      setQuoteId('');
      return;
    }
    getPresentableQuotes(dealId)
      .then((r) => {
        const qs = Array.isArray(r?.quotes) ? r.quotes : [];
        setPresentableQuotes(qs);
        setQuoteId((prev) => prev || (qs[0]?.id || ''));
      })
      .catch(() => { setPresentableQuotes([]); setQuoteId(''); });
  }, [open, slug, dealId]);

  // Re-render preview whenever the slug changes.
  useEffect(() => {
    if (!open || !canCompose || !slug) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setLoadingPreview(true);
    // Forward `extra` to /preview so the rendered body matches what the
    // /send POST will produce — particularly for pilot-audience
    // templates where ?pilot_id picks which pilot the preview targets.
    const load = useSession
      ? previewTemplate(sessionToken, slug, effectiveExtra)
      : previewTemplateForDeal(dealId, slug, effectiveExtra);
    load
      .then((r) => {
        if (cancelled) return;
        setPreview(r);
        setSubject(r?.subject || '');
        setRecipient(r?.to || '');
      })
      .catch((err) => {
        if (cancelled) return;
        const msg =
          err instanceof ApiError
            ? err.body?.error || err.code
            : err.message || 'failed';
        toast.error(`Preview failed: ${msg}`);
        setPreview(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });
    return () => {
      cancelled = true;
    };
    // extra is intentionally part of the dep list — a pilot switch
    // should re-render the preview against the newly-selected pilot
    // (pilot-reminder template renders body addressed to {firstName}
    // and resolves recipient email from the picked pilot).
  }, [open, sessionToken, dealId, useSession, canCompose, slug, extra?.pilot_id, extra?.pilot_crm_id, extra?.urgency, quoteId]);

  // Resolve the recipient's mobile so the SMS channel knows whether to enable
  // and what read-only number to show. Runs once a contact id is known (an
  // explicit pilot, or the deal's primary contact from the preview).
  useEffect(() => {
    if (!open) {
      setSmsPhone(null);
      setSmsOptedOut(false);
      setSmsResolved(false);
      return;
    }
    if (!smsContactId) {
      // Sheet is open but there is no contact to resolve a number from. Treat
      // as resolved-with-no-number so the SMS option disables (with tooltip)
      // instead of spinning "Resolving…" forever.
      setSmsPhone(null);
      setSmsOptedOut(false);
      setSmsResolved(true);
      return;
    }
    let cancelled = false;
    setSmsResolved(false);
    resolvePhone({ crm_contact_id: smsContactId, pilot_id: pilotId })
      .then((r) => {
        if (cancelled) return;
        setSmsPhone(r?.phone || null);
        setSmsOptedOut(!!r?.opted_out);
        setSmsResolved(true);
      })
      .catch(() => {
        if (cancelled) return;
        setSmsPhone(null);
        setSmsOptedOut(false);
        setSmsResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, smsContactId, pilotId]);

  // Prefill the SMS body from the per-slug default the first time SMS mode is
  // entered (broker-editable; never clobbers a body the broker has typed).
  useEffect(() => {
    if (channel !== 'sms') return;
    setSmsBody((prev) => (prev && prev.trim() ? prev : smsDefaultFor(slug, { firstName: smsFirstName })));
  }, [channel, slug, smsFirstName]);

  async function handleSendSMS() {
    if (!smsPhone || !smsBody.trim() || submitting) return;
    setSubmitting(true);
    try {
      const res = await sendSMS({
        phone: smsPhone,
        message: smsBody.trim(),
        crm_contact_id: smsContactId,
        crm_deal_id: preview?.deal_id || dealId || null,
        contact_name: preview?.toName || null,
      });
      toast.success(`Text sent to ${smsPhone}`);
      // The permission gate's shadow warnings (a number that texted STOP, no
      // permission on record) replace the old opted_out note (people-create-door T7c).
      toastSmsWarnings(res?.warnings);
      if (typeof onSent === 'function') onSent(res);
      onOpenChange?.(false);
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.body?.error || err.code : err.message || 'failed';
      toast.error(`Text failed: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSend() {
    if (channel === 'sms') return handleSendSMS();
    if (!canCompose || !slug || !recipient.trim() || !subject.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        slug,
        to: recipient.trim(),
        subject: subject.trim(),
        toName: preview?.toName || '',
        cc: preview?.cc || [],
        bcc: preview?.bcc || [],
        // Forward broker-selected pilot + other extras so the SEND
        // targets the same pilot the preview rendered for. Backend
        // tracon-send.routes.js accepts pilot_crm_id / urgency /
        // ai_name / ai_email on the body and uses them in
        // template.build(data).
        ...(extra?.pilot_crm_id || extra?.pilot_id
          ? { pilot_crm_id: extra.pilot_crm_id || extra.pilot_id }
          : {}),
        ...(extra?.urgency ? { urgency: extra.urgency } : {}),
        ...(extra?.ai_name ? { ai_name: extra.ai_name } : {}),
        ...(extra?.ai_email ? { ai_email: extra.ai_email } : {}),
        // §7 Slice 5 (gap B) — binding-app-ready carries the selected quote so
        // the checkout link gets ?quote=<id>.
        ...(effectiveExtra.quote_id ? { quote_id: effectiveExtra.quote_id } : {}),
      };
      const res = useSession
        ? await sendTemplate(sessionToken, payload)
        : await sendTemplateForDeal(dealId, payload);
      toast.success(`Sent ${res?.mode === 'override' ? '(override)' : ''} to ${res?.to || recipient}`);
      if (typeof onSent === 'function') onSent(res);
      onOpenChange?.(false);
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.body?.error || err.code : err.message || 'failed';
      toast.error(`Send failed: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  }

  // SMS is only offerable once we know a number is on file. While resolving,
  // the toggle stays clickable; it disables only when we know there is none.
  const smsUnavailable = smsResolved && !smsPhone;
  const smsTooLong = smsBody.length > SMS_MAX_LENGTH;

  const canSend =
    channel === 'sms'
      ? !!smsPhone && smsBody.trim().length > 0 && !smsTooLong && !submitting
      : canCompose &&
        !!slug &&
        recipient.trim().length > 0 &&
        subject.trim().length > 0 &&
        !submitting;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="max-h-[92vh] overflow-y-auto md:max-w-4xl"
      >
        <SheetHeader>
          <SheetTitle>{channel === 'sms' ? 'Send text' : 'Send email'}</SheetTitle>
          <SheetDescription>
            {channel === 'sms'
              ? 'Edit the message, then send a text. It logs to the timeline.'
              : 'Pick a template, review the preview, edit subject or recipient if needed, then send.'}
          </SheetDescription>
          {!useSession ? (
            <p className="mt-1 text-xs text-text-dim">
              No client session on this deal yet. Plain broker emails send
              straight out; a client-link email (renewal invitation, quotes
              ready) initializes this deal automatically when you send. No manual
              Initialize needed.
            </p>
          ) : null}
        </SheetHeader>

        {/* Channel selector (Gap 2). SMS is disabled with a tooltip when no
            mobile number is on file for the recipient. */}
        <div className="mt-4 inline-flex rounded-card border border-border bg-bg/40 p-0.5">
          {['email', 'sms'].map((ch) => {
            const isSms = ch === 'sms';
            const disabled = isSms && smsUnavailable;
            return (
              <button
                key={ch}
                type="button"
                disabled={disabled}
                onClick={() => setChannel(ch)}
                title={disabled ? 'No mobile number on file for this recipient' : undefined}
                className={`rounded-[10px] px-3 py-1 text-xs font-medium transition-colors ${
                  channel === ch
                    ? 'bg-accent text-white'
                    : disabled
                      ? 'cursor-not-allowed text-text-dim/50'
                      : 'text-text-muted hover:text-text'
                }`}
              >
                {isSms ? 'Text' : 'Email'}
              </button>
            );
          })}
        </div>

        {/* Subject + Recipient hoisted above the picker/preview grid so
            long values (e.g. "N6684P LLC", 2026-05-20 Band 0 polish)
            aren't visually clipped by the narrower right-column width. */}
        {channel === 'sms' ? (
          <div className="mt-4">
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
              Recipient
            </span>
            <div className="mt-1.5 rounded-card border border-border bg-surface px-3 py-2 text-sm text-text">
              {smsPhone || (smsResolved ? 'No mobile number on file' : 'Resolving number…')}
            </div>
            {smsOptedOut ? (
              <span className="mt-1 block text-[11px] text-warning">
                This number opted out of texts. Sending is your call.
              </span>
            ) : null}
          </div>
        ) : (
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
                Recipient
              </span>
              <Input
                type="email"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder={preview?.to || 'name@example.com'}
                className="mt-1.5"
              />
              {preview?.toName ? (
                <span className="mt-0.5 block truncate text-[10px] text-text-dim">
                  Default: {preview.toName} &lt;{preview.to}&gt;
                </span>
              ) : null}
            </label>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
                Subject
              </span>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Template default"
                className="mt-1.5"
              />
            </label>
          </div>
        )}

        {/* §7 Slice 5 (gap B) — "select & send checkout": the broker picks WHICH
            presented quote; the checkout link carries ?quote=<id> so the client
            lands straight in the bind wizard. (Email only.) */}
        {channel === 'email' && slug === 'binding-app-ready' ? (
          <div className="mt-4">
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Quote to send for checkout</span>
            {presentableQuotes.length === 0 ? (
              <p className="mt-1.5 text-xs text-text-dim">No client-visible quotes on this deal yet — log a quote and make it visible first.</p>
            ) : (
              <select
                value={quoteId}
                onChange={(e) => setQuoteId(e.target.value)}
                className="mt-1.5 w-full rounded-card border border-border bg-surface px-3 py-2 text-sm text-text"
              >
                {presentableQuotes.map((q) => (
                  <option key={q.id} value={q.id}>
                    {[q.carrier || 'Carrier', q.policy_type, q.premium ? formatCurrency(q.premium) : null].filter(Boolean).join(' · ')}
                  </option>
                ))}
              </select>
            )}
            <span className="mt-0.5 block text-[10px] text-text-dim">The client lands straight in the bind wizard for the selected quote.</span>
          </div>
        ) : null}

        {channel === 'sms' ? (
          <div className="mt-4 grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-medium uppercase tracking-wide text-text-muted">
                  Message
                </span>
                <span className={`text-[10px] ${smsTooLong ? 'text-warning' : 'text-text-dim'}`}>
                  {smsBody.length}/{SMS_MAX_LENGTH} · {smsSegmentCount} segment{smsSegmentCount === 1 ? '' : 's'}
                </span>
              </div>
              <textarea
                value={smsBody}
                onChange={(e) => setSmsBody(e.target.value.slice(0, SMS_MAX_LENGTH))}
                rows={8}
                placeholder="Type a text message…"
                className="w-full resize-none rounded-card border border-border bg-surface px-3 py-2 text-sm text-text"
              />
              <span className="block text-[10px] text-text-dim">
                A single text holds {SMS_SINGLE_SEGMENT} characters; longer messages send as multiple segments.
              </span>
            </div>
            <div className="space-y-3">
              <div className="rounded-card border border-border bg-bg/40 p-2">
                <div className="mb-1">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-text-muted">
                    Preview
                  </span>
                </div>
                <div className="h-[320px] md:h-[420px]">
                  <PreviewPane channel="sms" smsBody={smsBody} smsTo={smsPhone || ''} />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4 grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
            <div className="space-y-3">
              {loadingTemplates ? (
                <p className="text-sm text-text-muted">Loading templates…</p>
              ) : (
                <TemplatePicker
                  templates={templates || []}
                  value={slug}
                  onSelect={setSlug}
                  recommendedSlugs={derivedRecommended}
                />
              )}
            </div>
            <div className="space-y-3">
              <div className="rounded-card border border-border bg-bg/40 p-2">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-text-muted">
                    Preview
                  </span>
                  {preview?.override_active ? (
                    <span className="text-[10px] text-warning">
                      OVERRIDE → {preview.override_address}
                    </span>
                  ) : null}
                </div>
                <div className="h-[320px] md:h-[420px]">
                  <PreviewPane html={preview?.html} loading={loadingPreview} />
                </div>
              </div>
            </div>
          </div>
        )}

        {channel === 'email' && !useSession && preview?.will_initialize ? (
          <p className="mt-3 rounded-card border border-info/30 bg-info-soft px-3 py-2 text-xs text-info">
            Sending initializes this deal: a client session is created and the
            secure tower link is generated and embedded. No invite emails go out,
            only this message.
          </p>
        ) : null}

        <SheetFooter className="mt-4">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange?.(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSend} disabled={!canSend}>
            {submitting ? 'Sending…' : 'Send'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
